import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenantId } from './useTenantId';
import { toast } from 'sonner';

export function usePortalBranding() {
  const { tenantId } = useTenantId();
  const queryClient = useQueryClient();

  const { data: branding, isLoading } = useQuery({
    queryKey: ['portal-branding', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('portal_logo_url, portal_primary_color, portal_secondary_color, portal_font_family, portal_source_website, portal_last_auto_update')
        .eq('id', tenantId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `${tenantId}/logo-${Date.now()}.${fileExt}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('portal-branding')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('portal-branding')
        .getPublicUrl(fileName);

      // Update tenant record
      const { error: updateError } = await supabase
        .from('tenants')
        .update({ portal_logo_url: urlData.publicUrl })
        .eq('id', tenantId);

      if (updateError) throw updateError;

      return urlData.publicUrl;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-branding'] });
      toast.success('Logo uploaded successfully');
    },
    onError: (error: any) => {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    },
  });

  const updateColors = useMutation({
    mutationFn: async (colors: { primary?: string; secondary?: string; fontFamily?: string }) => {
      const updates: any = {};
      if (colors.primary !== undefined) updates.portal_primary_color = colors.primary;
      if (colors.secondary !== undefined) updates.portal_secondary_color = colors.secondary;
      if (colors.fontFamily !== undefined) updates.portal_font_family = colors.fontFamily;

      const { error } = await supabase
        .from('tenants')
        .update(updates)
        .eq('id', tenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-branding'] });
      toast.success('Branding colors updated');
    },
    onError: (error: any) => {
      console.error('Error updating colors:', error);
      toast.error('Failed to update colors');
    },
  });

  const applyAutoBranding = useMutation({
    mutationFn: async (branding: { 
      logo_url: string | null; 
      primary_color: string | null; 
      secondary_color: string | null;
      source_website: string;
    }) => {
      const updates: any = {
        portal_last_auto_update: new Date().toISOString(),
        portal_source_website: branding.source_website,
      };
      
      if (branding.logo_url) updates.portal_logo_url = branding.logo_url;
      if (branding.primary_color) updates.portal_primary_color = branding.primary_color;
      if (branding.secondary_color) updates.portal_secondary_color = branding.secondary_color;

      const { error } = await supabase
        .from('tenants')
        .update(updates)
        .eq('id', tenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-branding'] });
      toast.success('Auto-branding applied successfully');
    },
    onError: (error: any) => {
      console.error('Error applying auto-branding:', error);
      toast.error('Failed to apply auto-branding');
    },
  });

  const deleteLogo = useMutation({
    mutationFn: async () => {
      if (!branding?.portal_logo_url) return;

      // Extract file path from URL
      const url = new URL(branding.portal_logo_url);
      const pathParts = url.pathname.split('/');
      const fileName = pathParts.slice(-2).join('/'); // tenant_id/logo-xxx.ext

      // Delete from storage
      const { error: deleteError } = await supabase.storage
        .from('portal-branding')
        .remove([fileName]);

      if (deleteError) throw deleteError;

      // Update tenant record
      const { error: updateError } = await supabase
        .from('tenants')
        .update({ portal_logo_url: null })
        .eq('id', tenantId);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-branding'] });
      toast.success('Logo removed');
    },
    onError: (error: any) => {
      console.error('Error deleting logo:', error);
      toast.error('Failed to delete logo');
    },
  });

  return {
    branding,
    isLoading,
    uploadLogo: uploadLogo.mutateAsync,
    updateColors: updateColors.mutateAsync,
    deleteLogo: deleteLogo.mutateAsync,
    applyAutoBranding: applyAutoBranding.mutateAsync,
  };
}
